/**
 * DashboardTabs
 *
 * Builds the tab list (with FPR-only Teams/Results tabs surfaced based on
 * selected game type) and renders the active tab's content. Tab content is
 * delegated to dedicated panels; only the players tab and the FPR Teams tab
 * need to share player data, which is passed via props.
 */

import { useMemo } from 'react';
import { type Tab } from '@swtorbit/shared-components';
import { Alert, Tabs } from '@swtorbit/ui/react';
import { GameType, type GamePlayerInfo } from '@swtorbit/shared-socket-events';
import { useGameContext } from '../../contexts/GameContext';
import { GameSettings } from '../GameSettings';
import { RoundManagement } from '../RoundManagement';
import { EffectsSettings } from '../EffectsSettings';
import { NotificationsPanel } from '../NotificationsPanel';
import { DiscordSettings } from '../DiscordSettings';
import { TeamsTab, FprResultsTab } from '../fpr';
import { TrlTeamsTab, TrlResultsTab } from '../trl';
import { HnsResultsTab } from '../hns';
import { HnstTeamsTab, HnstResultsTab, HnstStatisticsTab, HnstCinematicsTab } from '../hnst';
import { PlayersPanel } from './PlayersPanel';
import type { DashboardTabId } from '../../hooks/useDashboardState';
import type { PlayerManagement } from '../../hooks/usePlayerManagement';

interface DashboardTabsProps {
  activeTab: DashboardTabId;
  onTabChange: (tab: DashboardTabId) => void;
  playerManagement: PlayerManagement;
  onEditRegistration: (player: GamePlayerInfo) => void;
  onOpenSeedModal: () => void;
}

export function DashboardTabs({
  activeTab,
  onTabChange,
  playerManagement,
  onEditRegistration,
  onOpenSeedModal,
}: DashboardTabsProps) {
  const { selectedGame, error, clearError } = useGameContext();
  const { players } = playerManagement;
  const isFprGame = selectedGame?.gameType === GameType.FP_RACE;
  const isHnsGame = selectedGame?.gameType === GameType.HIDE_AND_SEEK;
  const isTrlGame = selectedGame?.gameType === GameType.TRAILBLAZERS;
  const isHnstGame = selectedGame?.gameType === GameType.HIDE_AND_SEEK_TEAMS;

  const tabs: Tab[] = useMemo(() => {
    const baseTabs: Tab[] = [
      { id: 'settings', label: 'Game Settings', content: null },
      { id: 'rounds', label: 'Rounds', content: null },
    ];

    if (isFprGame) {
      baseTabs.push({ id: 'teams', label: 'Teams', content: null });
      baseTabs.push({ id: 'results', label: 'Results', content: null });
    } else if (isHnsGame) {
      baseTabs.push({ id: 'results', label: 'Results', content: null });
    } else if (isTrlGame || isHnstGame) {
      baseTabs.push({ id: 'teams', label: 'Teams', content: null });
      if (isHnstGame) baseTabs.push({ id: 'statistics', label: 'Statistics', content: null });
      baseTabs.push({ id: 'results', label: 'Results', content: null });
    }

    baseTabs.push({ id: 'effects', label: 'Effects', content: null });
    if (isHnstGame) baseTabs.push({ id: 'cinematics', label: 'Cinematics', content: null });
    baseTabs.push(
      { id: 'notifications', label: 'Notifications', content: null },
      { id: 'players', label: `Players (${players.length})`, content: null },
      { id: 'discord', label: 'Discord', content: null },
    );

    return baseTabs;
  }, [players.length, isFprGame, isHnsGame, isTrlGame, isHnstGame]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'settings':
        return <div className="w-100 align-self-stretch"><GameSettings /></div>;
      case 'rounds':
        return <RoundManagement />;
      case 'teams':
        return (
          <div className="w-100 align-self-stretch">
            {isTrlGame ? <TrlTeamsTab /> : isHnstGame ? <HnstTeamsTab /> : <TeamsTab players={players} />}
          </div>
        );
      case 'results':
        return (
          <div className="w-100 align-self-stretch">
            {isFprGame ? <FprResultsTab /> : isTrlGame ? <TrlResultsTab /> : isHnsGame ? <HnsResultsTab /> : isHnstGame ? <HnstResultsTab /> : null}
          </div>
        );
      case 'statistics':
        return isHnstGame ? <div className="w-100 align-self-stretch"><HnstStatisticsTab /></div> : null;
      case 'effects':
        return <div className="w-100 align-self-stretch"><EffectsSettings /></div>;
      case 'cinematics':
        return <div className="w-100 align-self-stretch"><HnstCinematicsTab /></div>;
      case 'notifications':
        return <NotificationsPanel />;
      case 'players':
        return (
          <PlayersPanel
            playerManagement={playerManagement}
            onEditRegistration={onEditRegistration}
            onOpenSeedModal={onOpenSeedModal}
          />
        );
      case 'discord':
        return <div className="w-100 align-self-stretch"><DiscordSettings /></div>;
      default:
        return null;
    }
  };

  return (
    <>
      {/* What the server refused (a round's Start, End, Load, …), until dismissed or the next round action. */}
      {error && <Alert hue="red" className="mb-sm" onDismiss={clearError}>{error}</Alert>}
        <Tabs center items={tabs.map((t) => ({ value: t.id, label: t.label }))} value={activeTab} onChange={(tabId) => onTabChange(tabId as DashboardTabId)}>{renderTabContent()}</Tabs>
    </>
  );
}
